<template>
  <div>
    <div class="d-flex justify-between align-center">
      <div class="d-flex">
        <AdditionalServiceInput
          id="serviceValue"
          :title="service.type === 'PERCENTAGE' ? $t('ADDITIONAL_SERV_FIELD_PERCENTAGE') : $t('ADDITIONAL_SERV_FIELD_BASE_PRICE')"
          :value="serviceValue"
          :min="adjustedMinValue"
          :max="adjustedMaxValue"
          :changeRate="service.changeRate"
          :rules="valueRules"
          :suffix="service.type === 'PERCENTAGE' ? '%' : '€'"
          @onInputChange="handleInputChange"
        />

        <BlockDiscountsDialog
          v-if="discountButtonMode !== 'hidden'"
          :mode="discountButtonMode" 
          @onChangeDiscountsLocked="toggleDiscountsLocked"
        />

        <template v-if="service.canExpire">
          <AdditionalServiceInput
            id="expireMonths"
            :title="$t('ADDITIONAL_SERV_FIELD_EXPIRE_MONTHS')"
            :value="serviceExpireMonths"
            :min="1"
            :max="6"
            :changeRate="1"
            :rules="valueRules"
            :suffix="$t('ADDITIONAL_SERV_SUFFIX_EXPIRE_MONTHS')"
            data-cy="expire-date"
            @onInputChange="handleInputChange"
          />
        </template>

        <template v-if="service.type === 'PERCENTAGE'">
          <AdditionalServiceInput
            v-if="service.initialMinValue > 0"
            id="minValue"
            :title="$t('ADDITIONAL_SERV_FIELD_MIN_QUANTITY')"
            :value="serviceMinValue"
            :max="service.initialMinValue"
            :rules="minRules"
            @onInputChange="handleInputChange"
          />
          <AdditionalServiceInput
            v-if="service.initialMaxValue > 0"
            id="maxValue"
            :title="$t('ADDITIONAL_SERV_FIELD_MAX_QUANTITY')"
            :value="serviceMaxValue"
            :min="service.initialMaxValue"
            :rules="maxRules"
            @onInputChange="handleInputChange"
          />
        </template>
      </div>

      <template v-if="shouldShowTrashIcon">
        <v-icon class="ml-2 mr-2 pointer" @click="handleIconClick">
          mdi-delete
        </v-icon>
      </template>
    </div>

    <div class="d-flex justify-end">
      <v-btn
        text
        color="primary"
        class="link non-text-transformation display-btn"
        :ripple="false"
        data-cy="cancel-changes-btn"
        @click="handleCancel"
      >
        {{ $t('ADDITIONAL_SERV_EXPANSION_BUTTON_CANCEL') }}
      </v-btn>

      <v-btn
        text
        color="primary"
        class="link non-text-transformation display-btn"
        :ripple="false"
        data-cy="save-changes-btn"
        @click="handleConfirm"
      >
        {{ $t('ADDITIONAL_SERV_EXPANSION_BUTTON_SAVE') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import AdditionalServiceInput from '@/containers/offer-form/additional-services/AdditionalServiceInput'
import BlockDiscountsDialog from '@/containers/offer-form/shipping/BlockDiscountsDialog'
import { mapGetters } from 'vuex'
import { CONTRACT_MODEL } from '@/helpers/constants'
import { GET_CONTRACT_MODEL } from '@/store/modules/offer/getters'

export default {
  name: 'PanelContentInput',
  components: { AdditionalServiceInput, BlockDiscountsDialog },
  props: {
    service: {
      type: Object,
      default: () => ({
        id: '',
        value: 0,
        minValue: 0,
        maxValue: 0,
        stdMinValue: 0,
        stdMaxValue: 0,
        stdMinValueApproved: 0,
        stdMaxValueApproved: 0,
        expireMonths: 6,
        type: '',
        canExpire: false,
        changeRate: 1,
        initialMinValue: 0,
        initialMaxValue: 0,
      }),
    },
  },
  data() {
    return {
      serviceValue: this.service.value,
      serviceMinValue: this.service.minValue,
      serviceMaxValue: this.service.maxValue,
      serviceExpireMonths: this.service.expireMonths,

      areDiscountsLocked: false,

      allowZero: false,
      suffix: this.service.type === 'PERCENTAGE' ? '%' : '€',

      valueRules: [
        value => value !== '' || this.$t('ADDITIONAL_SERV_RULE_MANDATORY'),
        value =>
          !this.adjustedMaxValue ||
          value <= this.adjustedMaxValue ||
          this.$t('ADDITIONAL_SERV_RULE_STD_MAX_VALUE', {
            value: this.adjustedMaxValue,
            suffix: this.suffix,
          }),
        value =>
          this.allowZero ||
          !this.adjustedMinValue ||
          value >= this.adjustedMinValue ||
          this.$t('ADDITIONAL_SERV_RULE_STD_MIN_VALUE', {
            value: this.adjustedMinValue,
            suffix: this.suffix,
          }),
      ],
      minRules: [
        value => value !== '' || this.$t('ADDITIONAL_SERV_RULE_MANDATORY'),
        value =>
          this.allowZero ||
          value <= this.service.initialMinValue ||
          this.$t('ADDITIONAL_SERV_RULE_MIN_VALUE', {
            value: this.service.initialMinValue,
          }),
      ],
      maxRules: [
        value => value !== '' || this.$t('ADDITIONAL_SERV_RULE_MANDATORY'),
        value =>
          value >= this.service.initialMaxValue ||
          this.$t('ADDITIONAL_SERV_RULE_MAX_VALUE', {
            value: this.service.initialMaxValue,
          }),
      ],
    }
  },
  computed: {
    ...mapGetters('offer', { contractModel: GET_CONTRACT_MODEL }),
    hasDiscountPossibility() {
      return (
        this.service.stdMinValue !== this.service.stdMinValueApproved ||
        this.service.stdMaxValue !== this.service.stdMaxValueApproved
      )
    },

    isPortugueseModel() {
      return this.contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT ||
             this.contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT_NEXUS ||
             this.contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2026_PT_NEXUS
    },

    shouldShowTrashIcon() {
      if (!this.isPortugueseModel) return this.service.stdMinValue > 0
      
      const portugueseServicesWithTrash = ['ALTDESTINO', 'ALTORIGEM', 'PONTOCTT']
      return portugueseServicesWithTrash.includes(this.service.id) || this.service.stdMinValue > 0
    },

    adjustedMinValue() {
      if (!this.hasDiscountPossibility) return this.service.stdMinValue
      return this.areDiscountsLocked
        ? this.service.stdMinValueApproved
        : this.service.stdMinValue
    },
    adjustedMaxValue() {
      if (!this.hasDiscountPossibility) return this.service.stdMaxValue
      return this.areDiscountsLocked
        ? this.service.stdMaxValueApproved
        : this.service.stdMaxValue
    },

    discountButtonMode() {
      if (!this.hasDiscountPossibility) return 'hidden'

      if (this.areDiscountsLocked) {
        const belowOrEqualMinApproved =
          this.serviceValue <= this.service.stdMinValueApproved
        const aboveOrEqualMaxApproved =
          this.serviceValue >= this.service.stdMaxValueApproved
        if (belowOrEqualMinApproved || aboveOrEqualMaxApproved) {
          return 'unlock'
        }
        return 'hidden'
      } else {
        return 'lock'
      }
    },
  },
  watch: {
  serviceValue(newVal, oldVal) {
    if (!this.hasDiscountPossibility) return
        if (this.areDiscountsLocked && newVal !== 0) {
          if (newVal < this.service.stdMinValueApproved) {
            this.serviceValue = this.service.stdMinValueApproved
            if (oldVal !== this.serviceValue) {
              this.$refs.blockDiscountsDialog && (this.$refs.blockDiscountsDialog.showDialog = true)
            }
          } else if (newVal > this.service.stdMaxValueApproved) {
            this.serviceValue = this.service.stdMaxValueApproved
            if (oldVal !== this.serviceValue) {
              this.$refs.blockDiscountsDialog && (this.$refs.blockDiscountsDialog.showDialog = true)
            }
          }
        }
      },
    },

  mounted() {
    if (this.hasDiscountPossibility) {
      this.areDiscountsLocked = true

      if (this.serviceValue < this.service.stdMinValueApproved) {
        this.serviceValue = this.service.stdMinValueApproved
      } else if (this.serviceValue > this.service.stdMaxValueApproved) {
        this.serviceValue = this.service.stdMaxValueApproved
      }
    }
  },
  methods: {
    handleInputChange(id, value) {
      if (id === 'serviceValue') {
        this.allowZero = value === 0
        this.serviceValue = value
      } else if (id === 'minValue') {
        this.serviceMinValue = value
      } else if (id === 'maxValue') {
        this.serviceMaxValue = value
      } else if (id === 'expireMonths') {
        this.serviceExpireMonths = value
      }
    },

    handleConfirm() {
      const updatedService = {
        ...this.service,
        value: this.serviceValue,
        minValue: this.serviceMinValue,
        maxValue: this.serviceMaxValue,
        expireMonths: this.serviceExpireMonths,
      }
      this.$emit('onSaveService', updatedService)
      this.$emit('onClose')
    },

    handleCancel() {
      this.$emit('onClose')
    },

    handleIconClick() {
      this.allowZero = true
      this.serviceValue = 0

      this.$emit('onInputChange', 'serviceValue', 0)
    },

    toggleDiscountsLocked(mode) {
      if (mode === 'unlock') {
        this.areDiscountsLocked = false
      } else {
        this.areDiscountsLocked = true

        if (this.serviceValue < this.service.stdMinValueApproved) {
          this.serviceValue = this.service.stdMinValueApproved
        } else if (this.serviceValue > this.service.stdMaxValueApproved) {
          this.serviceValue = this.service.stdMaxValueApproved
        }
      }
    },
  },
}
</script>

<style scoped>
.d-flex {
  display: flex;
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.align-center {
  align-items: center;
}

.ml-2 {
  margin-left: 16px;
}
.mr-2 {
  margin-right: 16px;
}

.pointer {
  cursor: pointer;
}
</style>
